import React, {useEffect, useMemo, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import moment from "moment";
import dashboardServices from "app/services/dashboard-services";
import {DASHBOARD_COLORS, neutralChipSx} from "./chartColors";

// Km planificados contra km reales de los viajes terminados, dia a dia.
//
// El plan es el que el chofer recibio al iniciar el viaje; lo real es la ruta por las
// paradas en el orden en que las cerro. Por eso lo real mide si respeto el orden, no
// por donde manejo entre una parada y otra.
//
// Solo suman los viajes con los dos lados medidos: sumar de un solo lado mostraria un
// ahorro que no existe. Lo que queda fuera se dice arriba del grafico.
const PLAN = DASHBOARD_COLORS.total;
const REAL = DASHBOARD_COLORS.picked;
const DESVIO = DASHBOARD_COLORS.manual;
const AHORRO = DASHBOARD_COLORS.finished;
const ACUMULADO = "#111827";

const ULTIMOS_30 = "last30";
const VISTA_DIFERENCIA = "diferencia";
const VISTA_PLAN_REAL = "plan_real";

const formatKm = (value) => (value === null || value === undefined)
    ? "-"
    : `${Number(value).toLocaleString("es-CL", {maximumFractionDigits: 1})} km`;
const formatNumero = (value) => Number(value).toLocaleString("es-CL", {maximumFractionDigits: 1});
const formatKmDiff = (value) => (value === null || value === undefined)
    ? "-"
    : `${value > 0 ? "+" : ""}${formatKm(value)}`;
const formatPct = (value) => (value === null || value === undefined)
    ? "-"
    : `${value > 0 ? "+" : ""}${Number(value).toLocaleString("es-CL", {maximumFractionDigits: 1})}%`;
const formatMoney = (value) => (value === null || value === undefined)
    ? "-"
    : `${value > 0 ? "+" : value < 0 ? "-" : ""}$${Math.abs(Math.round(Number(value))).toLocaleString("es-CL")}`;

// Ultimos 30 dias y los ultimos 12 meses, el actual primero. Si se llega desde Viajes
// con un mes mas viejo, ese mes tambien va en la lista para que el filtro lo muestre.
const opcionesPeriodo = (periodo) => {
    const meses = Array.from({length: 12}, (_, i) => moment().subtract(i, "months").format("YYYY-MM"));
    if (periodo !== ULTIMOS_30 && !meses.includes(periodo)) meses.push(periodo);

    return [
        {value: ULTIMOS_30, label: "Últimos 30 días"},
        ...meses.map((mes) => {
            const texto = moment(mes, "YYYY-MM").toDate().toLocaleDateString("es-CL", {month: "long", year: "numeric"});
            return {value: mes, label: texto.charAt(0).toUpperCase() + texto.slice(1)};
        })
    ];
};

const rangoDe = (periodo) => {
    if (periodo === ULTIMOS_30) {
        return {
            desde: moment().subtract(29, "days").format("YYYY-MM-DD"),
            hasta: moment().format("YYYY-MM-DD")
        };
    }
    const mes = moment(periodo, "YYYY-MM");
    return {
        desde: mes.clone().startOf("month").format("YYYY-MM-DD"),
        hasta: mes.clone().endOf("month").format("YYYY-MM-DD")
    };
};

// Un desvio chico es ruido de redondeo de HERE; desde 5% ya es un recorrido distinto.
const colorDesvio = (pct) => (pct !== null && pct !== undefined && pct > 5 ? DESVIO : "text.primary");

// Recorrer de mas es el problema; de menos, un ahorro (o una parada que se salto).
const colorDiferencia = (km) => (km > 0 ? DESVIO : AHORRO);

const redondear = (value) => Math.round(value * 10) / 10;

// Los dos ejes del grafico de diferencias incluyen el 0 y lo ponen a la misma altura:
// sin eso una barra negativa flota y el acumulado parece cruzar el 0 donde no lo hace.
const dominiosAlineados = (valoresDia, valoresAcumulado) => {
    const extremos = (valores) => {
        const limpios = valores.filter((v) => v !== null && v !== undefined);
        return [Math.min(0, ...limpios), Math.max(0, ...limpios)];
    };
    const ejes = [extremos(valoresDia), extremos(valoresAcumulado)];
    const negativo = Math.max(...ejes.map(([min, max]) => (max - min ? -min / (max - min) : 0)));

    return ejes.map(([min, max]) => {
        if (min === max) return [0, 1];
        const escala = Math.max(
            negativo < 1 ? max / (1 - negativo) : 0,
            negativo > 0 ? -min / negativo : 0
        );
        return [-negativo * escala, (1 - negativo) * escala];
    });
};

// Marcas redondas (1, 2, 2,5 o 5 por potencia de 10) dentro del dominio, con el 0 incluido.
const marcasDe = ([min, max]) => {
    const crudo = (max - min) / 4;
    if (!crudo) return [0];
    const potencia = 10 ** Math.floor(Math.log10(crudo));
    const paso = [1, 2, 2.5, 5, 10].map((m) => m * potencia).find((p) => p >= crudo);
    const marcas = [];
    for (let v = Math.ceil(min / paso) * paso; v <= max + 1e-9; v += paso) marcas.push(redondear(v));
    return marcas;
};

const textoNoRecorridas = (n) => `${n} ${n === 1 ? "parada no recorrida" : "paradas no recorridas"}`;

const KpiTile = ({label, value, detail, color}) => (
    <Box
        sx={{
            flex: "1 1 170px",
            p: 1.5,
            borderRadius: 2,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider"
        }}
    >
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h3" sx={{color: color || "text.primary", fontWeight: 700, my: 0.25}}>{value}</Typography>
        {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
    </Box>
);

const DiferenciaTooltip = ({active, payload, label}) => {
    if (!active || !payload?.length) return null;

    const fila = payload[0]?.payload || {};

    return (
        <Box
            sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)"
            }}
        >
            <Typography variant="subtitle2" sx={{mb: 0.75}}>{label}</Typography>
            {fila.trips ? (
                <>
                    <Typography variant="body2" sx={{color: colorDiferencia(fila.diff_km), fontWeight: 600}}>
                        {fila.diff_km > 0 ? "Km de más" : "Km de menos"}: {formatKmDiff(fila.diff_km)} ({formatPct(fila.diff_pct)})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Plan {formatKm(fila.planned_km)} · Real {formatKm(fila.actual_km)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {fila.trips} {fila.trips === 1 ? "viaje" : "viajes"}
                    </Typography>
                </>
            ) : (
                <Typography variant="body2" color="text.secondary">Sin viajes comparables</Typography>
            )}
            {fila.cumulative_km !== null && fila.cumulative_km !== undefined && (
                <Typography variant="body2" sx={{mt: 0.75, pt: 0.75, borderTop: "1px solid", borderColor: "divider", fontWeight: 600}}>
                    Acumulado del período: {formatKmDiff(fila.cumulative_km)}
                </Typography>
            )}
        </Box>
    );
};

const RouteComplianceTooltip = ({active, payload, label}) => {
    if (!active || !payload?.length) return null;

    const fila = payload[0]?.payload || {};
    const diff = (fila.actual_km !== null && fila.planned_km !== null && fila.planned_km !== undefined)
        ? fila.actual_km - fila.planned_km
        : null;

    return (
        <Box
            sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)"
            }}
        >
            <Typography variant="subtitle2" sx={{mb: 0.75}}>{label}</Typography>
            {fila.trips ? (
                <>
                    <Typography variant="body2" sx={{color: PLAN}}>Planificado: {formatKm(fila.planned_km)}</Typography>
                    <Typography variant="body2" sx={{color: REAL}}>Real: {formatKm(fila.actual_km)}</Typography>
                    <Typography variant="body2" sx={{mt: 0.75, pt: 0.75, borderTop: "1px solid", borderColor: "divider", fontWeight: 600}}>
                        Diferencia: {diff === null ? "-" : `${diff > 0 ? "+" : ""}${formatKm(diff)}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {fila.trips} {fila.trips === 1 ? "viaje" : "viajes"}
                    </Typography>
                </>
            ) : (
                <Typography variant="body2" color="text.secondary">Sin viajes comparables</Typography>
            )}
        </Box>
    );
};

const RouteCompliance = () => {
    // El icono de orden de Viajes llega con ?panel=km_planificado&driver_id=..&mes=YYYY-MM
    // para abrir el grafico ya filtrado por el conductor y el mes del viaje.
    const [searchParams] = useSearchParams();
    const [periodo, setPeriodo] = useState(() => (
        /^\d{4}-\d{2}$/.test(searchParams.get("mes") || "") ? searchParams.get("mes") : ULTIMOS_30
    ));
    const [driverId, setDriverId] = useState(() => searchParams.get("driver_id") || "");
    const [vista, setVista] = useState(VISTA_DIFERENCIA);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let vigente = true;
        setLoading(true);
        setError("");

        dashboardServices
            .getRouteCompliance({...rangoDe(periodo), driver_id: driverId})
            .then((res) => { if (vigente) setData(res); })
            .catch(() => { if (vigente) setError("No fue posible cargar los kilómetros planificados y reales."); })
            .finally(() => { if (vigente) setLoading(false); });

        return () => { vigente = false; };
    }, [periodo, driverId]);

    // El acumulado arrastra su valor en los dias sin viajes: ese dia no cambio nada, y
    // la linea plana lo dice. Antes del primer viaje no hay nada que acumular, y despues
    // de hoy tampoco: un mes en curso no tiene esos dias todavia.
    const chartData = useMemo(() => {
        let acumulado = null;
        const hoy = moment().format("YYYY-MM-DD");

        return (data?.series ?? []).map((row) => {
            const comparable = row.trips > 0 && row.planned_km !== null && row.actual_km !== null;
            const diff = comparable ? redondear(row.actual_km - row.planned_km) : null;
            if (comparable) acumulado = redondear((acumulado ?? 0) + diff);

            return {
                ...row,
                diff_km: diff,
                diff_pct: comparable && row.planned_km ? (diff / row.planned_km) * 100 : null,
                cumulative_km: row.date > hoy ? null : acumulado,
                label: new Date(`${row.date}T00:00:00`).toLocaleDateString("es-CL", {day: "2-digit", month: "short"})
            };
        });
    }, [data]);

    const totales = data?.totals;
    const choferes = data?.drivers ?? [];
    const filas = data?.by_driver ?? [];
    const conCosto = Boolean(data?.cost_configured);
    const comparados = totales?.compared_trips ?? 0;
    const registrados = totales?.recorded_trips ?? 0;

    const [dominioDia, dominioAcumulado] = useMemo(() => dominiosAlineados(
        chartData.map((fila) => fila.diff_km),
        chartData.map((fila) => fila.cumulative_km)
    ), [chartData]);

    // El que mas km agrego arriba. Sin viajes comparables no hay barra que mostrar.
    const ranking = useMemo(() => (
        (data?.by_driver ?? [])
            .filter((fila) => fila.compared_trips > 0)
            .map((fila) => ({...fila, diff_km: Number(fila.diff_km) || 0}))
            .sort((a, b) => b.diff_km - a.diff_km)
    ), [data]);

    const cargando = (
        <Stack alignItems="center" justifyContent="center" sx={{height: "100%"}}>
            <CircularProgress/>
        </Stack>
    );

    return (
        <Card
            sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                background: "linear-gradient(180deg, rgba(107,114,128,0.06) 0%, rgba(249,250,251,0.8) 100%)"
            }}
        >
            <CardContent sx={{p: {xs: 2, md: 3}}}>
                <Stack spacing={2.5}>
                    <Stack spacing={0.5}>
                        <Typography variant="h4">Kilómetros: planificado vs real</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Viajes terminados. Lo real es la ruta por las paradas en el orden en que el chofer las cerró,
                            y se compara con esas mismas paradas en el orden planificado: mide cuánto cuesta cambiar el
                            orden, no los desvíos entre una parada y otra.
                        </Typography>
                    </Stack>

                    <Stack direction={{xs: "column", lg: "row"}} spacing={1.5} alignItems={{lg: "center"}}>
                        <FormControl size="small" sx={{minWidth: 200}}>
                            <InputLabel>Período</InputLabel>
                            <Select
                                label="Período"
                                value={periodo}
                                onChange={(event) => setPeriodo(event.target.value)}
                            >
                                {opcionesPeriodo(periodo).map((opcion) => (
                                    <MenuItem key={opcion.value} value={opcion.value}>{opcion.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{minWidth: 240}}>
                            <InputLabel shrink>Conductor</InputLabel>
                            <Select
                                label="Conductor"
                                notched
                                displayEmpty
                                value={driverId}
                                onChange={(event) => setDriverId(event.target.value)}
                            >
                                <MenuItem value="">Todos los conductores</MenuItem>
                                {choferes.map((chofer) => (
                                    <MenuItem key={chofer.id} value={String(chofer.id)}>{chofer.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    {error && <Alert severity="error">{error}</Alert>}

                    {!loading && !error && registrados === 0 && (
                        <Alert severity="info">
                            Aún no hay viajes terminados con historial en este rango. El plan se guarda al iniciar
                            cada viaje y lo real al terminarlo, así que el gráfico se llena con los viajes de ahora en adelante.
                        </Alert>
                    )}

                    {totales && comparados > 0 && (
                        <Stack direction="row" flexWrap="wrap" gap={1.5}>
                            <KpiTile
                                label="Diferencia de km"
                                value={formatKmDiff(totales.diff_km)}
                                color={colorDiferencia(totales.diff_km)}
                                detail={`${formatPct(totales.diff_pct)} · plan ${formatNumero(totales.planned_km)} → real ${formatKm(totales.actual_km)}`}
                            />
                            <KpiTile
                                label="Paradas no recorridas"
                                value={totales.skipped_stops || 0}
                                color={totales.skipped_stops > 0 ? DESVIO : undefined}
                                detail={totales.skipped_stops > 0
                                    ? "Fuera de la comparación"
                                    : "Se cerraron todas las planificadas"}
                            />
                            <KpiTile
                                label="Viajes en el orden planificado"
                                value={`${totales.in_order_trips} de ${comparados}`}
                                detail={totales.in_order_pct === null ? undefined : `${Math.round(totales.in_order_pct)}% respetó el orden`}
                            />
                            {conCosto && totales.cost_diff !== null && (
                                <KpiTile
                                    label="Diferencia de costo"
                                    value={formatMoney(totales.cost_diff)}
                                    color={colorDiferencia(totales.cost_diff)}
                                    detail="Combustible y peajes del recorrido"
                                />
                            )}
                        </Stack>
                    )}

                    {!loading && registrados > comparados && (
                        <Typography variant="body2" color="text.secondary">
                            Se comparan {comparados} de {registrados} viajes terminados: al resto le falta el plan
                            (la ruta no se pudo calcular al iniciar) o el recorrido real.
                        </Typography>
                    )}

                    <Box sx={{bgcolor: "background.paper", borderRadius: 2, p: 1.5}}>
                        <Stack direction={{xs: "column", md: "row"}} justifyContent="space-between" spacing={1} sx={{mb: 1}}>
                            <Typography variant="h6">
                                {vista === VISTA_DIFERENCIA ? "Km de más o de menos por día" : "Km planificados y reales por día"}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <Chip
                                    size="small"
                                    label="Diferencia"
                                    clickable
                                    sx={neutralChipSx(vista === VISTA_DIFERENCIA)}
                                    onClick={() => setVista(VISTA_DIFERENCIA)}
                                />
                                <Chip
                                    size="small"
                                    label="Plan vs real"
                                    clickable
                                    sx={neutralChipSx(vista === VISTA_PLAN_REAL)}
                                    onClick={() => setVista(VISTA_PLAN_REAL)}
                                />
                            </Stack>
                        </Stack>

                        <Box sx={{height: 360}}>
                            {loading ? cargando : (
                                <ResponsiveContainer width="100%" height="100%">
                                    {vista === VISTA_DIFERENCIA ? (
                                        <ComposedChart data={chartData} margin={{top: 12, right: 8, left: 4, bottom: 8}}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false}/>
                                            <XAxis dataKey="label" tick={{fontSize: 12}} interval="preserveStartEnd"/>
                                            <YAxis
                                                yAxisId="dia"
                                                domain={dominioDia}
                                                ticks={marcasDe(dominioDia)}
                                                allowDataOverflow
                                                tick={{fontSize: 12}}
                                                label={{
                                                    value: "Km del día",
                                                    angle: -90,
                                                    position: "insideLeft",
                                                    style: {textAnchor: "middle", fill: "#6B7280", fontSize: 12}
                                                }}
                                            />
                                            {/* El acumulado crece mucho mas que un dia: en el mismo eje
                                                aplastaria las barras. */}
                                            <YAxis
                                                yAxisId="acumulado"
                                                orientation="right"
                                                domain={dominioAcumulado}
                                                ticks={marcasDe(dominioAcumulado)}
                                                allowDataOverflow
                                                tick={{fontSize: 12}}
                                                label={{
                                                    value: "Acumulado",
                                                    angle: 90,
                                                    position: "insideRight",
                                                    style: {textAnchor: "middle", fill: "#6B7280", fontSize: 12}
                                                }}
                                            />
                                            <Tooltip content={<DiferenciaTooltip/>} cursor={{fill: "rgba(107,114,128,0.08)"}}/>
                                            <Legend
                                                payload={[
                                                    {value: "Km de más", type: "square", color: DESVIO},
                                                    {value: "Km de menos", type: "square", color: AHORRO},
                                                    {value: "Acumulado del período", type: "line", color: ACUMULADO}
                                                ]}
                                            />
                                            <ReferenceLine yAxisId="dia" y={0} stroke="#9CA3AF"/>
                                            <Bar yAxisId="dia" dataKey="diff_km" name="Diferencia" maxBarSize={28} radius={[3, 3, 3, 3]}>
                                                {chartData.map((fila) => (
                                                    <Cell key={fila.date} fill={colorDiferencia(fila.diff_km)}/>
                                                ))}
                                            </Bar>
                                            <Line
                                                yAxisId="acumulado"
                                                type="stepAfter"
                                                dataKey="cumulative_km"
                                                name="Acumulado del período"
                                                stroke={ACUMULADO}
                                                strokeWidth={2}
                                                dot={false}
                                                activeDot={{r: 4}}
                                            />
                                        </ComposedChart>
                                    ) : (
                                        <LineChart data={chartData} margin={{top: 12, right: 16, left: 4, bottom: 8}}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB"/>
                                            <XAxis dataKey="label" tick={{fontSize: 12}} interval="preserveStartEnd"/>
                                            <YAxis
                                                label={{
                                                    value: "Kilómetros",
                                                    angle: -90,
                                                    position: "insideLeft",
                                                    style: {textAnchor: "middle", fill: "#6B7280", fontSize: 12}
                                                }}
                                            />
                                            <Tooltip content={<RouteComplianceTooltip/>}/>
                                            <Legend/>
                                            {/* Los dias sin viajes quedan como hueco: unirlos dibujaria un
                                                dia en que se planifico y se hizo algo que nadie hizo. */}
                                            <Line
                                                type="monotone"
                                                dataKey="planned_km"
                                                name="Planificado"
                                                stroke={PLAN}
                                                strokeWidth={3}
                                                strokeDasharray="6 4"
                                                dot={{r: 3}}
                                                activeDot={{r: 6}}
                                                connectNulls={false}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="actual_km"
                                                name="Real"
                                                stroke={REAL}
                                                strokeWidth={3}
                                                dot={{r: 3}}
                                                activeDot={{r: 6}}
                                                connectNulls={false}
                                            />
                                        </LineChart>
                                    )}
                                </ResponsiveContainer>
                            )}
                        </Box>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={5}>
                            <Box sx={{height: 390, bgcolor: "background.paper", borderRadius: 2, p: 1.5}}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 1}}>
                                    <Typography variant="h6">Km de más por conductor</Typography>
                                    {driverId && (
                                        <Button size="small" onClick={() => setDriverId("")}>Ver todos</Button>
                                    )}
                                </Stack>
                                <Box sx={{height: 320}}>
                                    {loading ? cargando : ranking.length === 0 ? (
                                        <Stack alignItems="center" justifyContent="center" sx={{height: "100%"}}>
                                            <Typography variant="body2" color="text.secondary">
                                                Sin datos en el rango seleccionado
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={ranking}
                                                layout="vertical"
                                                margin={{top: 4, right: 16, left: 8, bottom: 4}}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false}/>
                                                <XAxis type="number" tick={{fontSize: 12}} unit=" km"/>
                                                <YAxis type="category" dataKey="name" width={120} tick={{fontSize: 12}}/>
                                                <Tooltip
                                                    cursor={{fill: "rgba(107,114,128,0.08)"}}
                                                    formatter={(value, _name, item) => [
                                                        `${formatKmDiff(value)} (${formatPct(item.payload.diff_pct)})`,
                                                        [
                                                            `${item.payload.compared_trips} ${item.payload.compared_trips === 1 ? "viaje" : "viajes"}`,
                                                            item.payload.skipped_stops > 0 ? textoNoRecorridas(item.payload.skipped_stops) : null
                                                        ].filter(Boolean).join(", ")
                                                    ]}
                                                />
                                                <ReferenceLine x={0} stroke="#9CA3AF"/>
                                                <Bar
                                                    dataKey="diff_km"
                                                    maxBarSize={22}
                                                    radius={[3, 3, 3, 3]}
                                                    cursor="pointer"
                                                    onClick={(fila) => fila?.driver_id && setDriverId(String(fila.driver_id))}
                                                >
                                                    {ranking.map((fila) => (
                                                        <Cell key={fila.driver_id ?? "sin-conductor"} fill={colorDiferencia(fila.diff_km)}/>
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={7}>
                            <Box sx={{height: 390, bgcolor: "background.paper", borderRadius: 2, p: 1.5}}>
                                <Typography variant="h6" sx={{mb: 1.5}}>Detalle por conductor</Typography>
                                <TableContainer sx={{maxHeight: 320}}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Conductor</TableCell>
                                                <TableCell align="right">Viajes</TableCell>
                                                <TableCell align="right">Plan</TableCell>
                                                <TableCell align="right">Real</TableCell>
                                                <TableCell align="right">Dif.</TableCell>
                                                <TableCell align="right">En orden</TableCell>
                                                {conCosto && <TableCell align="right">Costo extra</TableCell>}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loading ? (
                                                <TableRow>
                                                    <TableCell colSpan={conCosto ? 7 : 6} align="center">Cargando...</TableCell>
                                                </TableRow>
                                            ) : filas.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={conCosto ? 7 : 6} align="center">
                                                        Sin datos en el rango seleccionado
                                                    </TableCell>
                                                </TableRow>
                                            ) : filas.map((fila) => (
                                                <TableRow
                                                    key={fila.driver_id ?? "sin-conductor"}
                                                    hover
                                                    onClick={() => fila.driver_id && setDriverId(String(fila.driver_id))}
                                                    sx={{cursor: fila.driver_id ? "pointer" : "default"}}
                                                >
                                                    <TableCell>{fila.name}</TableCell>
                                                    <TableCell align="right">
                                                        {fila.compared_trips === fila.recorded_trips
                                                            ? fila.compared_trips
                                                            : `${fila.compared_trips}/${fila.recorded_trips}`}
                                                    </TableCell>
                                                    <TableCell align="right">{formatKm(fila.planned_km)}</TableCell>
                                                    <TableCell align="right">{formatKm(fila.actual_km)}</TableCell>
                                                    <TableCell align="right" sx={{color: colorDesvio(fila.diff_pct), fontWeight: 600}}>
                                                        {formatPct(fila.diff_pct)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {fila.in_order_pct === null ? "-" : `${Math.round(fila.in_order_pct)}%`}
                                                    </TableCell>
                                                    {conCosto && (
                                                        <TableCell align="right">{formatMoney(fila.cost_diff)}</TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {!loading && !conCosto && filas.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{display: "block", mt: 1}}>
                                        Para ver el costo de la desviación, configura el costo del combustible en la
                                        configuración de ruteo.
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default RouteCompliance;
